import { useEffect, useState } from "react";
import { TextField, Button, CircularProgress } from "@mui/material";
import { postRFSData } from "../../../components/Api/Post.js";
import ClassSearch from "../components/ClassSearch.js";
import { useAtom } from "jotai";
import {
	scannerRequestStateAtom,
	advancedSettingsAtom,
	examRequestErrorStateAtom,
} from "../../../components/Atoms/atoms.js";
import AdvancedSettingsModal from "./AdvancedSettingsModal.js";
import NumberOfQuestionsInputs from "./components/NumberOfQuestionsInputs.js";
import { RESET } from "jotai/utils";
import { serializeScannerStateForApi } from "../utils/common.js";

export default function MultipleChoiceExam(props) {
	const {
		navigate,
		ccid,
		token,
		inputProps,
		handlePrint,
		classNumList,
		instructorClasses,
		confirmedOfficeInstructor,
		handleDoneOfficeInstructor,
		ccidString,
		editMode,
		onEditSubmit,
	} = props;
	const [state, setState] = useAtom(scannerRequestStateAtom);
	const testing = false;
	const [loadingSubmit, setLoadingSubmit] = useState(false);

	//const [classNumHelpText, setClassNumHelpText] = useState(" ");
	const [notesError, setNotesError] = useState(false);
	const [notesHelpText, setNotesHelpText] = useState(" ");
	// const [multiple, setMultiple] = useState(false);
	const notesText =
		"Enter your comments here: Omitted questions, multiple answers accepted for a question, etc. Please do not include pre-grading correspondence here - TSQS will not see this note until we begin grading your exam.";

	const [advancedSettings, setAdvancedSettings] = useAtom(advancedSettingsAtom);

	const [errorState, setErrorState] = useAtom(examRequestErrorStateAtom);

	useEffect(() => {
		if (advancedSettings.compositeScoring) {
			setState({ ...state, e: Number(state.nKeys) + 1 });
		} else if (!advancedSettings.compositeScoring && advancedSettings.mrscor) {
			setState({ ...state, e: "1" });
		} else {
			setState({ ...state, e: "0" });
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.nKeys, advancedSettings.compositeScoring]);

	useEffect(() => {
		if (Number(state.nKeys) > 1) {
			setState({ ...state, c: "1" });
		} else {
			setState({ ...state, c: "0" });
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.nKeys]);

	useEffect(() => {
		if (advancedSettings.formulaScoring) {
			setState({ ...state, f: "1" });
		} else {
			setState({ ...state, f: "0" });
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [advancedSettings.formulaScoring]);

	const handleMCExamChange = (event) => {
		const value = event.target.value;
		const fieldName = event.target.name;

		if (fieldName === "lqNum" && value <= 250 && value >= 0) {
			setErrorState((prev) => ({ ...prev, lqNum: false }));
			setState({ ...state, [fieldName]: value });
		}
		if (fieldName === "hqNum" && value <= 250 && value >= 0) {
			setErrorState((prev) => ({ ...prev, hqNum: false }));
			setState({ ...state, [fieldName]: value });
		} else if (fieldName === "hqNum") {
			setErrorState((prev) => ({ ...prev, hqNum: true }));
		}
		if (fieldName === "class_ids") {
			setErrorState((prev) => ({ ...prev, classNum: false }));
			setState({ ...state, [fieldName]: value });
			// setClassNumHelpText(" ");
		}
		if (fieldName !== "lqNum" && fieldName !== "hqNum" && fieldName !== "class_ids") {
			setState({ ...state, [fieldName]: value });
		}
	};

	const handleChange = (event) => {
		const value = event.target.value;
		setState({
			...state,
			[event.target.name]: value,
		});

		if (event.target.name === "notes") {
			setNotesError(false);
			setNotesHelpText(" ");
		}
		if (event.target.name === "nKeys") {
			setErrorState((prev) => ({ ...prev, nKeys: false }));
		}
	};

	const postData = async () => {
		const payload = serializeScannerStateForApi(
			state,
			classNumList,
			ccid,
			ccidString,
			confirmedOfficeInstructor,
		);

		const response = await postRFSData(token, payload);

		return response;
	};

	const canSubmit =
		Number(state.lqNum) >= 1 &&
		Number(state.hqNum) > 0 &&
		Number(state.hqNum) <= 250 &&
		state.instructorName.length > 4 &&
		state.deptID !== "" &&
		state.email1 !== "" &&
		(state.delivery !== "" ||
			((state.delivery !== "Courier" || state.delivery !== "Pick Up") && state.address !== "")) &&
		(state.class_ids > 0 || state.class_ids.length > 0);

	const handleSubmit = async () => {
		setLoadingSubmit(true);
		if (canSubmit) {
			try {
				if (!editMode) {
					let response = await postData();
					if (response && response.reqnum) {
						let reqnum = response.reqnum;
						await handlePrint(reqnum);
						if (confirmedOfficeInstructor) {
							handleDoneOfficeInstructor(false);

							setState(RESET);
							setAdvancedSettings(RESET);
							setLoadingSubmit(false);
							if (!testing) window.location.reload();
						} else {
							setState(RESET);
							setAdvancedSettings(RESET);
							setLoadingSubmit(false);
							if (!testing) navigate("/success");
						}
					} else {
						setLoadingSubmit(false);
						throw new Error("Invalid response from server. Please try again.");
					}
				} else {
					await onEditSubmit();
					setLoadingSubmit(false);
				}
			} catch (error) {
				console.error("Error during submission:", error);
				const backendError =
					error?.response?.data?.error ||
					(typeof error?.response?.data === "string" ? error.response.data : null);
				const validationError =
					!backendError && error?.response?.data && typeof error.response.data === "object"
						? JSON.stringify(error.response.data)
						: null;
				setLoadingSubmit(false);
				alert(
					`There was an error submitting your request, please try again. If the problem persists, please contact TSQS for assistance.${backendError || validationError ? `\n\nDetails: ${backendError || validationError}` : ""}`,
				);
			}
		} else {
			alert("Please fill out all required fields.");
			setLoadingSubmit(false);
		}
	};

	useEffect(() => {
		if (advancedSettings.compositeScoring) {
			if (state.nKeys >= "6") {
				setState({ ...state, nKeys: "5" });
			}
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [advancedSettings.compositeScoring]);

	useEffect(() => {
		if (advancedSettings.formulaScoring) {
			if (state.nKeys >= "5") {
				setState({ ...state, nKeys: "4" });
			}
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [advancedSettings.formulaScoring]);

	return (
		<div className="relative w-full">
			<div className="absolute top-0 right-0 border-2 rounded-md hover:bg-gray-100">
				<AdvancedSettingsModal
					handleMCExamChange={handleMCExamChange}
					navigate={navigate}
					token={token}
					ccid={ccid}
					classNumList={classNumList}
					handlePrint={handlePrint}
					instructorClasses={instructorClasses}
					confirmedOfficeInstructor={confirmedOfficeInstructor}
					handleDoneOfficeInstructor={handleDoneOfficeInstructor}
					ccidString={ccidString}
				/>
			</div>

			<div className="flex flex-col  gap-4 pt-8">
				<NumberOfQuestionsInputs handleMCExamChange={handleMCExamChange} />

				<ClassSearch
					state={state}
					setState={setState}
					errorState={errorState}
					setErrorState={setErrorState}
					classNumList={classNumList}
					instructorClasses={instructorClasses}
				/>

				<div className="flex flex-col gap-2">
					<label htmlFor="notes" className="font-medium text-primary">
						Additional Comments for Grading:
					</label>

					<TextField
						inputProps={inputProps}
						multiline
						value={state.notes}
						error={notesError}
						helperText={notesHelpText}
						onChange={handleChange}
						name="notes"
						placeholder={notesText}
						className="w-full"
					/>
				</div>
				<Button
					variant="contained"
					color="primary"
					onClick={handleSubmit}
					className="w-full col-span-2"
					disabled={loadingSubmit || !canSubmit}
				>
					{loadingSubmit ? (
						<CircularProgress size={24} color="inherit" />
					) : editMode ? (
						"Update Request"
					) : (
						"Submit"
					)}
				</Button>
			</div>
		</div>
	);
}
